import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ocFetchConfig, type OcPortalConfig } from "./lib/oc-api";
export type { OcPortalConfig } from "./lib/oc-api";
import OcPortalLayout from "./components/OcPortalLayout";
import OcStep1Identifier from "./pages/OcStep1Identifier";
import OcStep2Channel from "./pages/OcStep2Channel";
import OcStep3Verify from "./pages/OcStep3Verify";
import OcStep4Password from "./pages/OcStep4Password";
import OcStepDone from "./pages/OcStepDone";
import OcDisabledPortal from "./pages/OcDisabledPortal";

// ─── Shared flow state ──────────────────────────────────────────────────────

export interface OcFlowState {
  step: "identifier" | "channel" | "verify" | "password" | "done" | "disabled" | "error";
  identifier?: string;
  sessionId?: string | null;
  allowedChannels?: Array<{ channel: "email" | "sms"; target: string }>;
  selectedChannel?: "email" | "sms";
  errorMessage?: string;
}

export interface OcStepProps {
  config: OcPortalConfig;
  state: OcFlowState;
  setState: React.Dispatch<React.SetStateAction<OcFlowState>>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function ocReadSlugFromPath(): string {
  const segments = window.location.pathname.split("/").filter(Boolean);
  return segments[0] ?? "";
}

// Tiny inline error fallback for generic failures.
function OcErrorView({ message }: { message?: string }) {
  return (
    <div className="min-h-full w-full bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm ring-1 ring-slate-200 p-8 text-center">
        <h1 className="text-lg font-semibold text-slate-900">Something went wrong</h1>
        <p className="mt-2 text-sm text-slate-600">
          {message ?? "We couldn't load this page. Please try again in a moment."}
        </p>
      </div>
    </div>
  );
}

function OcLoadingView() {
  return (
    <div className="min-h-full w-full bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="text-sm text-slate-500">Loading…</div>
    </div>
  );
}

// ─── App ────────────────────────────────────────────────────────────────────

export default function App() {
  const ocSlug = useMemo(() => ocReadSlugFromPath(), []);
  const [ocState, ocSetState] = useState<OcFlowState>({ step: "identifier" });

  const ocConfigQuery = useQuery({
    queryKey: ["oc-selfservice-config", ocSlug],
    queryFn: () => ocFetchConfig(ocSlug),
    enabled: ocSlug.length > 0,
  });

  if (!ocSlug) {
    return <OcDisabledPortal />;
  }

  if (ocConfigQuery.isLoading) {
    return <OcLoadingView />;
  }

  if (ocConfigQuery.isError) {
    return <OcErrorView />;
  }

  const result = ocConfigQuery.data;
  if (!result) {
    return <OcErrorView />;
  }

  // The worker returns 404 plain-text "Self-service is not available..." when
  // the slug is unknown or the master toggle is off; the fetcher coalesces
  // that into { error: "server_error" }. Any error field on the response
  // means we should render the neutral disabled-portal page.
  if (result.error) {
    return <OcDisabledPortal />;
  }

  const config = result as OcPortalConfig;
  if (typeof config.tenantName !== "string") {
    return <OcErrorView />;
  }

  const stepProps: OcStepProps = { config, state: ocState, setState: ocSetState };

  if (ocState.step === "disabled") {
    return <OcDisabledPortal />;
  }
  if (ocState.step === "error") {
    return <OcErrorView message={ocState.errorMessage} />;
  }

  return (
    <OcPortalLayout config={config}>
      {ocState.step === "identifier" && <OcStep1Identifier {...stepProps} />}
      {ocState.step === "channel" && <OcStep2Channel {...stepProps} />}
      {ocState.step === "verify" && <OcStep3Verify {...stepProps} />}
      {ocState.step === "password" && <OcStep4Password {...stepProps} />}
      {ocState.step === "done" && <OcStepDone {...stepProps} />}
    </OcPortalLayout>
  );
}
